import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { CommandPalette } from './Overview.jsx';
import ChatV2Modal from './ChatV2Modal.jsx';
import { useStore, attachConnectivityListeners } from './lib/store.js';
import { startStream } from './lib/stream.js';
import { OmniChordHelpSheet } from './components/OmniChordHelpSheet/OmniChordHelpSheet.jsx';

// Shell owns the top app bar, palette state, chat modal, and the
// app-wide hotkeys (Ctrl/⌘+K and Ctrl/⌘+. for palette, Escape for
// palette + chat dismissal). Per-route hotkeys (the `/` focus-search
// binding) stay owned by the page components.
//
// JOBE-0045.3: Shell is also the single mount point for the
// multiplexed /api/stream connection. State (me / agents / activity)
// reads from the Zustand store rather than per-Shell useState.
export function Shell() {
  const me = useStore((s) => s.me) || { name: 'Shawn', initials: 'SL' };
  const agents = useStore((s) => s.agents.list);
  const agentsLoaded = useStore((s) => s.agents.loaded);
  const activity = useStore((s) => s.activity);
  const online = useStore((s) => s.online);
  const loadMe = useStore((s) => s.loadMe);
  const loadAgents = useStore((s) => s.loadAgents);
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [chatTo, setChatTo] = useState(null);
  // JOBE-0098.5: help sheet open flag (the `?` chord opens it).
  const [helpSheetOpen, setHelpSheetOpen] = useState(false);
  // g-prefix chord: tracks whether the user has pressed `g` outside a
  // text field and is waiting for the second key of a Vim-style nav chord.
  // A 1 s timeout clears the pending state so a lone `g` doesn't linger.
  const gPendingRef = useRef(false);
  const gTimeoutRef = useRef(null);
  // JOBE-0098.5: registration slot for the active OmniPalette's
  // imperative handle. The Library route (or any future host) registers
  // either a React ref object (preferred — survives handle-identity
  // churn from `useImperativeHandle` re-runs) OR a snapshot handle, via
  // the outlet-context `registerOmniPalette` hook; unregisters on
  // unmount. Null when no host route is mounted — `⌘F` then falls
  // through to browser Find; `⌘,` / `/` no-op.
  //
  // We resolve at call time via `getPaletteHandle()` so an upstream
  // handle that React rebuilt (e.g. `useImperativeHandle(ref, fn, [open])`
  // where `open` flipped) is still observed correctly: the ref object's
  // `.current` is always the latest assignment from React, while a
  // captured snapshot would point at the original handle whose `isOpen()`
  // closure observes the original `open=false` forever.
  const omniPaletteRegistration = useRef(null);
  const registerOmniPalette = useCallback((refOrHandle) => {
    if (omniPaletteRegistration.current && refOrHandle) {
      // Single-palette assumption (v1: only /library mounts one). Warn
      // so a second concurrent host surfaces immediately instead of as
      // a mystery last-write-wins race.
      // eslint-disable-next-line no-console
      console.warn('[Shell] registerOmniPalette called while a palette is already registered; clobbering.');
    }
    omniPaletteRegistration.current = refOrHandle;
  }, []);
  const unregisterOmniPalette = useCallback(() => {
    omniPaletteRegistration.current = null;
  }, []);
  // Resolve the current handle from the registration slot. Accepts
  // either a React ref ({ current }) or a bare handle. Returning null
  // when no host is registered, or when the registered ref points at
  // null (e.g. between forwardRef mount + useImperativeHandle binding).
  const getPaletteHandle = useCallback(() => {
    const reg = omniPaletteRegistration.current;
    if (!reg) return null;
    // Heuristic: a React ref object has a `current` property; an
    // imperative handle exposes the action methods directly.
    if (typeof reg === 'object' && 'current' in reg && typeof reg.open !== 'function') {
      return reg.current || null;
    }
    return reg;
  }, []);
  // SPA / BFF / operator versions render as three independently-deployed
  // segments. SPA is baked in at build time (vite.config.js `define`);
  // BFF + op come over the wire as one aggregated /api/status response
  // (JOBE-0046). A flaky operator yields op=null so the footer can
  // still render BFF + SPA without blanking the whole row.
  const [buildInfo, setBuildInfo] = useState(null);

  // navigator on/offline events drive the offline chip + body class.
  // The store also flips `online` on direct fetch outcomes from the
  // outbox worker, so this is one of two signals.
  useEffect(() => attachConnectivityListeners(), []);

  // Toggle a body class for the offline background shade. Done at the
  // body element rather than via a wrapper div so the entire viewport
  // (including modal scrim) picks up the darker --paper-3 override.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('offline', !online);
  }, [online]);

  useEffect(() => {
    let alive = true;
    fetch('/api/status', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive && j) setBuildInfo(j); })
      .catch(() => { /* leave null; footer renders SPA alone */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => { loadAgents(); }, [loadAgents]);

  // Open the multiplexed stream once for the lifetime of the Shell.
  // startStream() returns a refcounted stop function; returning it
  // from the effect lets React run it as cleanup on unmount (and on
  // StrictMode's double-invoke in dev), so the EventSource closes
  // cleanly. The Outlet's children consume events via the store
  // rather than holding their own EventSource instances.
  useEffect(() => {
    return startStream();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || e.isComposing) return;
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey &&
          (e.key.toLowerCase() === 'k' || e.key === '.')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      // JOBE-0098.5: ⌘, — open the omni palette when a host has
      // registered it. With no host, the chord no-ops (there's no
      // browser-default to preserve). `getPaletteHandle()` resolves
      // the latest handle from the registration slot at call time, so
      // a stale `useImperativeHandle` snapshot can't bite us.
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey &&
          e.key === ',') {
        const handle = getPaletteHandle();
        if (!handle) return;
        e.preventDefault();
        handle.open();
        return;
      }
      // JOBE-0098.5: ⌘F / Ctrl+F — open the omni palette and capture
      // browser Find. Per the spec's "Decision: ⌘F vs browser Find"
      // pin: capture only when a palette host is mounted, otherwise
      // fall through (no preventDefault) so the browser's native Find
      // still fires on legacy / un-rolled-out routes.
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey &&
          e.key.toLowerCase() === 'f') {
        const handle = getPaletteHandle();
        if (!handle) return;
        e.preventDefault();
        handle.open();
        return;
      }
      // JOBE-0098.5: `/` (Vim-style focus) and `?` (help sheet) are
      // gated on the focus target — they must not intercept characters
      // the user is typing into an <input> / <textarea> / contentEditable.
      // The ⌘-modified chords above don't need this guard because the
      // modifier disambiguates from text entry.
      if (e.key === '/' && !isInTextField(e.target)) {
        const handle = getPaletteHandle();
        if (!handle) return;
        e.preventDefault();
        handle.open();
        return;
      }
      if (e.key === '?' && !isInTextField(e.target)) {
        e.preventDefault();
        setHelpSheetOpen(true);
        return;
      }
      // g-prefix nav chords (Vim-style): `g` arms the prefix; the second
      // key fires the navigation. Currently: `g l` → /library,
      // `g c` → /organization (JOBE-0159.3: was /contacts per JOBE-0122),
      // `g e` → /entities (JOBE-0168 Entity Browser).
      // The guard below prevents stealing `g` from text-entry surfaces.
      if (e.key === 'g' && !isInTextField(e.target) &&
          !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        gPendingRef.current = true;
        clearTimeout(gTimeoutRef.current);
        gTimeoutRef.current = setTimeout(() => { gPendingRef.current = false; }, 1000);
        return;
      }
      if (gPendingRef.current) {
        gPendingRef.current = false;
        clearTimeout(gTimeoutRef.current);
        if (e.key === 'l' && !isInTextField(e.target) &&
            !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
          e.preventDefault();
          navigate('/library');
          return;
        }
        if (e.key === 'c' && !isInTextField(e.target) &&
            !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
          e.preventDefault();
          // JOBE-0159.3: g c now navigates to /organization (was /contacts).
          navigate('/organization');
          return;
        }
        // JOBE-0168: g e → /entities (Entity Browser).
        if (e.key === 'e' && !isInTextField(e.target) &&
            !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
          e.preventDefault();
          navigate('/entities');
          return;
        }
        // Any other second key cancels the pending g-prefix without action.
        return;
      }
      if (e.key === 'Escape') {
        setPaletteOpen(false);
        setChatTo(null);
        setHelpSheetOpen(false);
        gPendingRef.current = false;
        clearTimeout(gTimeoutRef.current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const openChat = useCallback((to) => setChatTo(to), []);

  return (
    <div className="shell">
      <div className="frame">
        <div className="B">
          <TopBar me={me} today="Fri · Apr 17" online={online} onOpenPalette={() => setPaletteOpen(true)} />
          <Outlet context={{
            me,
            agents: agentsLoaded ? agents : null,
            activity,
            onOpenChat: openChat,
            registerOmniPalette,
            unregisterOmniPalette,
          }} />
        </div>
      </div>
      <VersionFooter spa={SPA_VERSION} bff={buildInfo?.bff} op={buildInfo?.op} />

      {chatTo && (
        <ChatV2Modal chatTo={chatTo} onClose={() => setChatTo(null)} />
      )}
      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} onOpenChat={openChat} />}
      {helpSheetOpen && <OmniChordHelpSheet onClose={() => setHelpSheetOpen(false)} />}
    </div>
  );
}

// JOBE-0098.5: vim-style chord guard. `/` and `?` are bare keys
// (no modifier), so we must let them pass through when the user is
// typing inside a text-entry surface — otherwise the chord would steal
// the keystroke from the input.
function isInTextField(target) {
  if (!target || typeof target !== 'object') return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

function TopBar({ me, today, online, onOpenPalette }) {
  return (
    <div className="topB">
      <div className="brand">Jobe</div>
      {/* JOBE-0159.6 canonical nav, amended 2026-05-24:
          Overview restored as the first/default tab (renders the legacy
          briefing + chat-launch surface; canonical landing at `/`).
          Order: Overview · Command Center · Organization · PromptOps · Projects · Library · Applications · Admin
          React Router v7 NavLink sets aria-current="page" automatically on the active link. */}
      <nav role="navigation" aria-label="Primary">
        <NavLink
          to="/"
          end
          className={({ isActive }) => (isActive ? 'on' : undefined)}
        >Overview</NavLink>
        <NavLink
          to="/command-center"
          className={({ isActive }) => (isActive ? 'on' : undefined)}
        >Command Center</NavLink>
        {/* JOBE-0159.3: Contacts folded into Organization; nav label updated. */}
        <NavLink
          to="/organization"
          className={({ isActive }) => (isActive ? 'on' : undefined)}
        >Organization</NavLink>
        {/* JOBE-0159.4: PromptOps top-level nav entry. */}
        <NavLink
          to="/promptops"
          className={({ isActive }) => (isActive ? 'on' : undefined)}
        >PromptOps</NavLink>
        <NavLink
          to="/projects"
          className={({ isActive }) => (isActive ? 'on' : undefined)}
        >Projects</NavLink>
        <NavLink
          to="/library"
          className={({ isActive }) => (isActive ? 'on' : undefined)}
        >Library</NavLink>
        {/* JOBE-0168: Entities (Entity Browser) — secondary nav tier,
            rendered immediately below Library. The Entity Browser is the
            power-user cross-type surface; the Library is the daily
            document landing. */}
        <NavLink
          to="/entities"
          className={({ isActive }) => (isActive ? 'on' : undefined)}
        >Entities</NavLink>
        <NavLink
          to="/applications"
          className={({ isActive }) => (isActive ? 'on' : undefined)}
        >Applications</NavLink>
        {/* JOBE-0118 + JOBE-0076.7.3: single Admin entry for all admin sub-tabs
            (Dispatcher, Agents, Channels, Eval Metrics). Prefix-match keeps the
            link active across all /admin/* sub-routes. */}
        <NavLink
          to="/admin"
          className={({ isActive }) => (isActive ? 'on' : undefined)}
        >Admin</NavLink>
      </nav>
      <button className="cmd" onClick={onOpenPalette}>
        <span className="glyph">⌖</span>
        <span>Ask, jump, invoke…</span>
        <kbd>⌘K</kbd>
      </button>
      <div className="right">
        {online === false
          ? <span className="offlineChip" title="The Jobe BFF is unreachable. Sends are queued.">offline</span>
          : <span>{today}</span>}
        <span className="me" title={me.name}>{me.initials}</span>
      </div>
    </div>
  );
}

// SPA bundle version is baked into the JS at build time by vite.config.js's
// `define`. Resolves to a {commit, shortCommit, modified, buildDate} object
// or "unknown" sentinels when git wasn't available at build time. typeof
// guard keeps the module importable from tests / SSR where the define
// substitution didn't run.
const SPA_VERSION = (typeof __JOBE_SPA_VERSION__ !== 'undefined')
  ? __JOBE_SPA_VERSION__
  : { commit: 'unknown', shortCommit: 'unknown', modified: false, buildDate: '' };

// VersionFooter shows three independently-deployed components: SPA bundle
// (most direct signal that a publish-spa landed), BFF, and operator. The
// SPA bundle's full commit + build date go in the title attr so they're
// available for copy-paste without cluttering the visible row.
function VersionFooter({ spa, bff, op }) {
  const segments = [
    formatSpa(spa),
    formatBuild('bff', bff),
    formatBuild('op', op),
  ].filter(Boolean);
  if (segments.length === 0) return null;
  const title = segments.map((s) => s.tooltip).join('\n');
  return (
    <div className="versionFooter" title={title}>
      {segments.map((seg, i) => (
        <span key={seg.label} className={`vfSeg vfSeg-${seg.label}`}>
          {i > 0 ? ' · ' : null}
          <span className="vfLabel">{seg.label}</span>{' '}
          <span className="vfValue">{seg.value}</span>
        </span>
      ))}
    </div>
  );
}

function formatSpa(spa) {
  if (!spa) return null;
  const short = spa.shortCommit || (spa.commit ? spa.commit.slice(0, 7) : '');
  if (!short || short === 'unknown') return null;
  const tooltip = `spa ${spa.commit || short}${spa.modified ? '+dirty' : ''}${spa.buildDate ? ' (' + spa.buildDate + ')' : ''}`;
  return { label: 'spa', value: short + (spa.modified ? '+dirty' : ''), tooltip };
}

function formatBuild(label, info) {
  if (!info) return null;
  // Prefer commit (always present when ldflags stamp the binary) over
  // version (which may be `dev` outside a tagged build). Fall back to the
  // human-readable version string if commit is missing.
  if (info.commit) {
    const tooltip = `${label} ${info.commit}${info.modified ? '+dirty' : ''}${info.buildDate ? ' (' + info.buildDate + ')' : ''}`;
    return { label, value: info.commit.slice(0, 7) + (info.modified ? '+dirty' : ''), tooltip };
  }
  if (info.version) {
    return { label, value: info.version, tooltip: `${label} ${info.version}` };
  }
  return null;
}
